"use client";

import { Menu as HeadlessMenu } from "@headlessui/react";
import { ArrowDownIcon } from "@ngg/icons";
import { DEFAULT_SORT } from "@server/utils/parseLoop/const";
import { useMemo } from "react";
import type { Sort, SortingOptions } from "types";

import { cn } from "@/lib/utils";

type Props = {
  options: SortingOptions;
  value?: Sort;
  initialValue?: Sort;
  onChange: (payload: Sort) => void;
  classNames?: {
    openButton?: string;
    items?: string;
  };
};

export default function SortBy(props: Props) {
  const {
    value = props.initialValue ?? DEFAULT_SORT,
    options,
    onChange,
    classNames,
  } = props;

  const actualValue = useMemo(
    () => options.find((opt) => opt.id === value),
    [value, options],
  );
  return (
    <div className="flex h-10 flex-1 justify-end">
      <HeadlessMenu as="div" className="h-full md:relative">
        <HeadlessMenu.Button
          className={({ open }) =>
            cn(
              "relative flex h-full w-full items-center justify-end gap-2 whitespace-nowrap rounded-none border-l border-transparent px-3 tracking-wide",
              // Focus styles
              "focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-4 focus-visible:outline-black",
              // After element styles
              "after:pointer-events-none after:absolute after:-bottom-px after:left-0 after:right-0 after:block after:h-px",
              open && "ui-open:border-grey-150 ui-open:after:bg-white",
              open && classNames?.openButton,
            )
          }
        >
          {({ open }) => (
            <>
              <span className="hover:underline">{actualValue?.label}</span>{" "}
              <ArrowDownIcon
                className={cn(
                  "h-4 w-4 flex-shrink-0 text-lg",
                  open && "ui-open:rotate-180",
                )}
              />
            </>
          )}
        </HeadlessMenu.Button>

        <HeadlessMenu.Items
          as="ul"
          className={cn(
            "relative right-0 z-20 mt-px border-b border-l border-grey-150 bg-white py-0 text-base",
            classNames?.items,
          )}
        >
          {options.map((option) => (
            <HeadlessMenu.Item as="li" key={option.id}>
              <button
                type="button"
                onClick={() => onChange(option.id)}
                className={cn(
                  "ui-active:bg-gray-100 flex w-full whitespace-nowrap px-3 py-2.5 text-left",
                  value === option.id && "underline",
                )}
              >
                {option.label}
              </button>
            </HeadlessMenu.Item>
          ))}
        </HeadlessMenu.Items>
      </HeadlessMenu>
    </div>
  );
}
